import { useHistory } from "react-router";
import { usePostHog } from "posthog-js/react";
import { LoadingScreen } from "components/audience-map/components/LoadingScreen";
import { MapErrorScreen } from "components/audience-map/components/LoadingScreen/MapErrorScreen";
import { MAP_TYPES, CAMPAIGN_INTERNAL_STATUSES, CAMPAIGN_CHANNELS_MIN_RECIPIENTS } from "pages/constants";
import { CAMPAIGN_SUBTYPES_ENUM } from "shared/constants";
import { SelectedRoute, Route, DistributionLocation, Coordinates, RouteMetadata } from "module/eddm/dto";
import { CampaignExtraDataPayload } from "module/campaign/dto";
import { LocationLatLngType } from "components/place-autocomplete/type";
import { VisiblePostCodeType } from "store/types";
import { useTheme, useMediaQuery } from "@material-ui/core";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { useEffect, useState, useRef, useMemo, useContext } from "react";
import { NextButton } from "components/next-button";
import { CustomTooltipContent } from "components/tooltip-content";
import cn from "classnames";
import { LightTooltip } from "components/light-tooltip";
import { ProgressBarContext } from "components/progress-bar";
import { checkIsNeedUpdateLastActiveStepState } from "utils";
import { generatePath } from "react-router-dom";
import { EDDM_ROUTING } from "config/routing";
import { radiusToZoom } from "./utils";
import { createQuote, updateCampaignExtraData, updateSelfServeCampaignData, updateCampaign } from "../../../../graphQL";
import { IntersectedRoutesMap, IntersectedRoute } from "./types";
import { MAP_EXTRA_STYLES } from "../../options";
import "./mapV3.css";

/**
 * Viewport of last resort for the EDDM map (continental US). `store.map` starts
 * with a zoom but NO center, and `<GoogleMap center={undefined}>` never
 * initialises a viewport — the container renders full-size with zero tiles,
 * which reads to the user as "the map doesn't load". Every load path must end
 * with a concrete center, so this is the final fallback when the campaign has
 * no saved location and the country is not (yet) known.
 */
const EDDM_FALLBACK_CENTER = { lat: 38.7883, lng: -106.5195 };
import { RouteStatisticsV2 } from "./components/RouteStatistics/RouteStatisticsV2";
import { AreaStatisticsV2 } from "./components/RouteStatistics/AreaStatisticsV2";
import { AddressBlockV3 } from "./components/AddressesBlock/AddressBlockV3";
import { useEDDMCostsCalculation } from "../../../../Hooks/useEDDMCostsCalculation";
import {
  onMapInit,
  renderPolygonsOnEddmMap,
  handleHoverSelectedMissionUpdate,
  renderCircleSelectorOnMap,
  removeCircleFromMap,
  calculateCircleRadius,
  removeSelectedRoutesFromSuggested,
} from "../../utils.js";

import { useStore } from "../../../../store";

interface AudienceMapEDDMV2Props {
  radius: number;
  setRadius: (radius: number) => void;
  selectedRoutes: SelectedRoute[];
  setSelectedRoutes: (selectedRoutes: SelectedRoute[] | ((selectedRoutes: SelectedRoute[]) => SelectedRoute[])) => void;
  setShouldAutoSave: (shouldAutoSave: boolean) => void;
  drawingMode: null | string;
  setDrawingMode: (drawingMode: null | string) => void;
  circleSelectorRadius: number;
  isUndoOrRedo: boolean;
  setIsUndoOrRedo: (isUndoOrRedo: boolean) => void;
  routes: Record<string, Route>;
  setRoutes: (routes: Record<string, Route> | Record<string, unknown>) => void;
  fetchRoutesAndUpdateMap: (focusedLocation: DistributionLocation, radius: number) => void;
  /** Re-runs the last carrier-route lookup; wired to the error state's Retry control. */
  retryFetchRoutes?: () => void;
  /** Aborts the in-flight carrier-route lookup; wired to the spinner's Cancel control. */
  cancelFetchRoutes?: () => void;
  setCircleSelectorRadius: (selectorRadius: number) => void;
  isCalculatingMultipleSelection: boolean;
  setIsCalculatingMultipleSelection: (calculatingMultipleSelection: boolean) => void;
  setIsSingleRouteSelection: (isSingleSelection: boolean) => void;
  googleMap: GoogleMap;
  setGoogleMap: (googleMap: null | GoogleMap) => void;
  onPlaceChanged: (location: LocationLatLngType) => void;
  getFilteredPostCodes?: (key: string) => VisiblePostCodeType[];
  shouldHideStatistics: boolean;
  setShouldHideStatistics: (shouldHideStatistics: boolean) => void;
}
export const AudienceMapEDDMV3 = ({
  radius,
  setRadius,
  selectedRoutes,
  setSelectedRoutes,
  setShouldAutoSave,
  drawingMode,
  setDrawingMode,
  circleSelectorRadius,
  isUndoOrRedo,
  setIsUndoOrRedo,
  routes,
  setRoutes,
  fetchRoutesAndUpdateMap,
  retryFetchRoutes,
  cancelFetchRoutes,
  setCircleSelectorRadius,
  isCalculatingMultipleSelection,
  setIsCalculatingMultipleSelection,
  setIsSingleRouteSelection,
  googleMap,
  setGoogleMap,
  onPlaceChanged,
  getFilteredPostCodes,
  shouldHideStatistics,
  setShouldHideStatistics,
}: AudienceMapEDDMV2Props) => {
  const posthog = usePostHog();
  const {
    country,
    map: {
      center,
      zoom,
      loading,
      loadingPercentage,
      loadingError = null,
      loadingErrorDetail = null,
      loadingNotice = null,
    },
    campaign: { isSubmitted, internalStatus, id: campaignId, lastActiveStep, subtype },
    client: { id: clientId, name: clientName },
    user,
    updateMap,
    hoveredRoute,
    setHoveredRoute,
    distributionLocations,
    setDistributionLocations,
    setFocusedLocation,
    costsCalculationData,
    suggestionsEnabled,
    selectedTargets,
    suggestedRoutes,
    setSuggestedRoutes,
    setSuggestionsEnabled,
    setDismissedSuggestedRoutes,
  } = useStore();
  const { recalculateEDDMCampaignCost } = useEDDMCostsCalculation();
  const { totalCost, tax } = costsCalculationData?.detailedCost || {};
  const { costPerFlyer } = costsCalculationData || {};
  const isCircleSelectorActive = drawingMode === MAP_TYPES.CIRCLE;

  const focusedLocation = useMemo(
    () => distributionLocations.find((location) => location.isFocused),
    [distributionLocations]
  );
  const [googleMapKey, setGoogleMapKey] = useState(new Date().getTime());
  const [mousePositionCoords, setMousePositionCoords] = useState<{ x: number; y: number } | null>();

  const runProgressBar = useContext(ProgressBarContext);
  const history = useHistory();
  const [isCalculatingRoutesIntercection, setIsCalculatingRoutesIntercection] = useState(false);
  const isZoomedInForStatistics = zoom >= 10;

  // useRef
  const clickSaveButtonRef = useRef(false);
  const circleFeatureRef = useRef(null);
  const intersectedRoutesRef = useRef(null);

  // useMemo
  const isNextActive = useMemo(() => {
    return (
      !!costsCalculationData.flyersAmount &&
      costsCalculationData.flyersAmount >= CAMPAIGN_CHANNELS_MIN_RECIPIENTS[CAMPAIGN_SUBTYPES_ENUM.EDDM]
    );
  }, [costsCalculationData.flyersAmount, isSubmitted]);

  const TooltipContent = useMemo(() => {
    if (!isNextActive) {
      return (
        <CustomTooltipContent
          title="Not enough addresses!"
          content={`To proceed, please select at least ${CAMPAIGN_CHANNELS_MIN_RECIPIENTS[
            CAMPAIGN_SUBTYPES_ENUM.EDDM
          ].toLocaleString()} residencies.`}
        />
      );
    }

    return "";
  }, [isNextActive]);

  // useCallback
  const saveCampaign = async (isAutoSave?: boolean) => {
    if (!isSubmitted && distributionLocations) {
      const payloadCampaignExtraData: CampaignExtraDataPayload = {
        campaignId,
        totalCosts: totalCost,
        flyersCount: costsCalculationData?.flyersAmount || 0,
        taxes: tax,
      };

      const isNeedUpdateLastActiveStep = checkIsNeedUpdateLastActiveStepState({
        stateLastActiveStep: lastActiveStep,
        newLastActiveStep: "segments",
        channel: subtype,
      });

      if (isNeedUpdateLastActiveStep) {
        payloadCampaignExtraData.lastActiveStep = "segments";
      }

      await updateCampaignExtraData(payloadCampaignExtraData);
      await updateSelfServeCampaignData({
        campaignId: campaignId,
        distributionLocations: distributionLocations,
      });
      if (isNeedUpdateLastActiveStep && !isAutoSave) {
        updateCampaign({ lastActiveStep: "segments" });
      }
      await setQuoteNumber(campaignId);
      if (!isAutoSave) {
        nextBtnClicked(campaignId);
      }
    }
  };

  const setQuoteNumber = async (campaignId: string) => {
    const quote = await createQuote(campaignId);
    updateCampaign({ quote });
  };

  const headerActions = {
    HOME: {
      action: () => {
        console.log("redirect home");
      },
    },
    NEXT: {
      isActive: true,
      action: async () => {
        if (isSubmitted) {
          runProgressBar(60);
          nextBtnClicked(campaignId);
          runProgressBar(80);
          runProgressBar(-1);
        } else {
          clickSaveButtonRef.current = false;
          await saveCampaign();
        }
      },
      hintPlacement: "bottom",
      hint: "",
    },
  };

  const nextBtnClicked = (id: string) => {
    // Always navigate to Flyer page (Design + Tracking combined)
    return history.push(generatePath(EDDM_ROUTING.EDDM_FLYER, { campaignId: id, clientId }));
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(768));

  // `googleMap` is only set once the Maps JS API has loaded, so it is the
  // reactive signal for "a Geocoder can be constructed". `window.google` is
  // not reactive: as a dep it never re-evaluates, so the memo could stay null
  // for the lifetime of the page and the centering effect below would never
  // get a geocoder.
  const geocoder = useMemo(
    () => (window?.google?.maps?.Geocoder ? new window.google.maps.Geocoder() : null),
    [googleMap]
  );

  // Resolve a center for the map exactly once per load, in priority order:
  // the campaign's own first saved location -> the client's country -> the
  // continental-US fallback. Previously this only geocoded `country.name`, and
  // only when the campaign had NO routes, so two very common states deep-loaded
  // with `center === undefined` and painted nothing: (a) a campaign WITH saved
  // routes (the geocode branch was skipped and nothing else set a center), and
  // (b) a brand-new draft opened before `country` had hydrated (`country` starts
  // as `{}`, `geocode({ address: undefined })` never returns OK, and `country`
  // was not a dependency so the effect never retried). Writing the result to the
  // store instead of calling `googleMap.setCenter` imperatively keeps the
  // `center` prop and the map in agreement across re-renders.
  useEffect(() => {
    if (center || !googleMap) return;

    const savedLocation = focusedLocation ?? distributionLocations?.[0];
    if (savedLocation?.lat && savedLocation?.lng) {
      updateMap({
        center: { lat: savedLocation.lat, lng: savedLocation.lng },
        zoom: radiusToZoom[savedLocation.radius] ?? 12,
      });
      return;
    }

    if (geocoder && country?.name) {
      geocoder.geocode({ address: country.name }, (results, status) => {
        const location = status === "OK" ? results?.[0]?.geometry?.location : null;
        updateMap({
          center: location ? { lat: location.lat(), lng: location.lng() } : EDDM_FALLBACK_CENTER,
          zoom: 5,
        });
      });
      return;
    }

    updateMap({ center: EDDM_FALLBACK_CENTER, zoom: 4 });
  }, [center, googleMap, geocoder, country?.name, distributionLocations, focusedLocation]);

  useEffect(() => {
    handleHoverSelectedMissionUpdate({ mapInit: googleMap, hoveredMission: hoveredRoute?.id || "" });
  }, [hoveredRoute]);

  useEffect(() => {
    if (Object.values(routes).length) {
      // LOOK FOR ALL THE SELECTED ROUTES IN NON FOCUSED LOCATION
      const selectedRoutesFromNonFocusedLocations = distributionLocations.reduce(
        (acc: SelectedRoute[], curr: DistributionLocation) => {
          if (curr.isFocused) {
            return acc;
          }
          return [...acc, ...curr.selectedRoutes];
        },
        []
      );

      // LOOK FOR THE SELECTED ROUTES THAT ARE PART OF THE ROUTES FETCHED FOR THE CURRENT FOCUSED LOCATIONS
      // AND REMOVE THE ROUTES THAT ARE ALREADY SELECTED IN SOME OTHER LOCATION
      const selectedRoutesOnCurrentFocusedLocation = Object.values(routes)
        .reduce((acc: SelectedRoute[], curr: Route) => {
          const selectedRoute: SelectedRoute | undefined = selectedRoutes.find((r: SelectedRoute) => r.id === curr.id);
          if (selectedRoute) {
            acc.push(selectedRoute);
          }
          return acc;
        }, [])
        .filter((r) => !selectedRoutesFromNonFocusedLocations.find((route) => route.id === r.id));

      // REMOVE ALL THE SELECTED ROUTES FROM THE SUGGESTED ONES IF NEEDED
      removeSelectedRoutesFromSuggested({
        selectedRoutes,
        suggestedRoutes,
        suggestionsEnabled,
        setSuggestionsEnabled,
        setSuggestedRoutes,
        googleMap,
      });

      // SAVE SELECTED ROUTES ON FOCUSED LOCATION AND REMOVE THE UNSELECTED ROUTES FROM OTHER LOCATIONS
      setDistributionLocations(
        distributionLocations.map((l) => {
          if (l.isFocused) {
            return { ...l, selectedRoutes: selectedRoutesOnCurrentFocusedLocation };
          } else {
            return {
              ...l,
              selectedRoutes: l?.selectedRoutes?.filter((r) => selectedRoutes.find((route) => route.id === r.id)) || [],
            };
          }
        })
      );
    }
  }, [selectedRoutes, routes]);

  useEffect(() => {
    // SEARCH NEW ROUTES DEPENDING ON SEARCHED LOCATION AND SELECTED RADIUS
    if (focusedLocation) {
      fetchRoutesAndUpdateMap(focusedLocation, focusedLocation.radius);
    }
  }, [focusedLocation?.name]);

  useEffect(() => {
    if (routes && Object.values(routes).length && googleMap) {
      renderPolygonsOnEddmMap({
        googleMap,
        routes,
        handleRouteSelect,
        selectedRoutes,
        isSubmitted,
        setHoveredRoute,
        setIsCalculatingMultipleSelection,
        intersectedRoutesRef,
        handleMultipleRoutesSelect,
        posthog,
        posthogBasePayload:
          user?.id && clientName && navigator.userAgent
            ? { userId: user?.id, clientName, device: navigator.userAgent }
            : null,
        setMousePositionCoords,
        selectedTargets,
        suggestedRoutes,
        suggestionsEnabled,
        isV3: true,
      });
      updateMap({ zoom: radiusToZoom[radius] });

      // Route is the default tool: a fresh map never switches itself to the
      // Area brush. Only re-draw the brush when the user already had it on.
      if (!isCircleSelectorActive || suggestionsEnabled) return;

      renderCircleSelectorOnMap({
        googleMap,
        circleSelectorRadius,
        circleFeatureRef,
        intersectedRoutesRef,
        setIsCalculatingRoutesIntercection,
        setMousePositionCoords,
      });
      setCircleSelectorRadius(calculateCircleRadius(radius));
    }
  }, [googleMap]);

  useEffect(() => {
    if (drawingMode === MAP_TYPES.CIRCLE && googleMap) {
      renderCircleSelectorOnMap({
        googleMap,
        circleSelectorRadius,
        circleFeatureRef,
        intersectedRoutesRef,
        setIsCalculatingRoutesIntercection,
        setMousePositionCoords,
      });
    } else if (drawingMode !== MAP_TYPES.CIRCLE && googleMap) {
      removeCircleFromMap({ googleMap, circleFeatureRef, setHoveredRoute, setMousePositionCoords });
    }
  }, [drawingMode, circleSelectorRadius]);

  useEffect(() => {
    // RESET GOOGLE MAP ON EVERY ROUTES FETCH
    if (focusedLocation?.selectedRoutes?.length && Object.values(routes).length) {
      const selectedLocationsToDeleteInFocusedLocation = focusedLocation.selectedRoutes.filter(
        (route) => !Object.values(routes).find((r) => r.id === route.id)
      );
      if (selectedLocationsToDeleteInFocusedLocation.length) {
        setSelectedRoutes((selectedRoutes: SelectedRoute[]): SelectedRoute[] => {
          return selectedRoutes.filter(
            (route) => !selectedLocationsToDeleteInFocusedLocation.find((r) => r.id === route.id)
          );
        });
      }
    }
    setGoogleMap(null);
    setGoogleMapKey(new Date().getTime());
  }, [routes]);

  useEffect(() => {
    recalculateEDDMCampaignCost();
    if (!distributionLocations?.length) {
      setRoutes({});
    }
  }, [distributionLocations]);

  const handleRouteSelect = ({
    routeId,
    paths,
    selected,
    metadata,
  }: {
    routeId: string;
    paths: Coordinates;
    selected: boolean;
    metadata: RouteMetadata;
  }) => {
    if (isSubmitted) {
      return;
    }
    setShouldAutoSave(true);
    setIsUndoOrRedo(false);
    setIsSingleRouteSelection(true);
    setSelectedRoutes((routes: SelectedRoute[]): SelectedRoute[] => {
      const currentMissionIndex = routes.findIndex((r) => r.id === routeId);
      if (selected && currentMissionIndex === -1) {
        return [
          ...routes,
          {
            id: routeId,
            paths,
            type: MAP_TYPES.POLYGON,
            metadata,
          },
        ] as SelectedRoute[];
      } else if (!selected && currentMissionIndex !== -1) {
        return [...routes.slice(0, currentMissionIndex), ...routes.slice(currentMissionIndex + 1)];
      } else {
        return routes;
      }
    });
  };

  const handleMultipleRoutesSelect = ({
    newSelectedRoutesOnLocation,
  }: {
    newSelectedRoutesOnLocation: IntersectedRoutesMap;
  }) => {
    if (isSubmitted) {
      return;
    }
    setShouldAutoSave(true);
    setIsUndoOrRedo(false);
    setSelectedRoutes((routes: SelectedRoute[]): SelectedRoute[] => {
      const updatedRoutes: SelectedRoute[] = [...routes];
      newSelectedRoutesOnLocation.forEach((route: IntersectedRoute, routeId: string) => {
        if (!updatedRoutes.find((r: SelectedRoute) => r.id === routeId)) {
          updatedRoutes.push({
            id: routeId,
            paths: route.paths,
            type: MAP_TYPES.POLYGON,
            metadata: route.metadata,
          });
        }
      });
      return updatedRoutes;
    });
  };

  const onAddressCardClick = (address: DistributionLocation) => {
    setShouldAutoSave(true);
    setIsUndoOrRedo(false);
    setFocusedLocation(address);
    setRadius(address.radius);
  };

  const handleDeleteLocation = (deletedLocation: DistributionLocation) => {
    setShouldAutoSave(true);
    setIsUndoOrRedo(false);
    const newDistributionLocations = distributionLocations
      .filter((location) => location.name !== deletedLocation.name)
      .map((location, i, array) => {
        if (i === 0 && deletedLocation.isFocused) {
          setRadius(location.radius);
          return { ...location, isFocused: true };
        } else {
          return location;
        }
      });
    if (shouldHideStatistics && !newDistributionLocations.length) setShouldHideStatistics(false);
    if (!newDistributionLocations.length) {
      setDismissedSuggestedRoutes([]);
      updateMap({
        center: EDDM_FALLBACK_CENTER,
        zoom: 5,
      });
    }
    setDistributionLocations(newDistributionLocations);

    if (deletedLocation?.selectedRoutes?.length) {
      setSelectedRoutes((selectedRoutes) => {
        return selectedRoutes.filter((route) => !deletedLocation?.selectedRoutes.find((r) => r.id === route.id));
      });
    }
  };

  const handleDragEnd = () => {
    setShouldHideStatistics(false);
    document.addEventListener(
      "mouseup",
      (event) => {
        setMousePositionCoords({ x: event.offsetX, y: event.offsetY });
      },
      { once: true }
    );
  };

  return (
    <div className="h-full w-full">
      <div
        className={cn(
          "eddm-map-v3-wrapper relative flex h-full w-full flex-row flex-nowrap justify-center p-0",
          !distributionLocations.length && "no-locations grayscale"
        )}
      >
        {(loading || isCalculatingMultipleSelection) && (
          <div className="absolute top-0 flex h-full w-full items-center justify-center bg-background/25">
            <LoadingScreen
              loadingPercentage={loadingPercentage}
              isCalculatingMultipleSelection={isCalculatingMultipleSelection}
              // Only the route walk is cancellable; the multiple-selection
              // recalculation is local and finishes in milliseconds.
              onCancel={cancelFetchRoutes && !isCalculatingMultipleSelection ? cancelFetchRoutes : undefined}
            />
          </div>
        )}
        {!loading && !isCalculatingMultipleSelection && loadingError && (
          <div className="absolute top-0 flex h-full w-full items-center justify-center bg-background/25">
            <MapErrorScreen
              message={loadingError}
              detail={loadingErrorDetail}
              onRetry={() => {
                if (retryFetchRoutes) retryFetchRoutes();
              }}
            />
          </div>
        )}
        {!loading && !loadingError && loadingNotice && (
          <div
            className="pointer-events-none absolute inset-x-0 top-4 z-[100002] flex justify-center"
            data-copilot-readout="eddmMapLoadNotice"
          >
            <p className="rounded-md bg-card px-4 py-2 text-sm text-muted-foreground shadow">{loadingNotice}</p>
          </div>
        )}
        <GoogleMap
          key={googleMapKey}
          onLoad={(map) => {
            onMapInit({ newMap: map, mapInit: googleMap, setMapInit: setGoogleMap });
          }}
          mapContainerStyle={{
            width: "100vw",
            height: "100%",
            marginLeft: 0,
          }}
          onDragStart={() => {
            setShouldHideStatistics(true);
          }}
          onDragEnd={handleDragEnd}
          center={center}
          zoom={zoom}
          onZoomChanged={() => {
            // @ts-ignore
            if (googleMap && googleMap.getZoom() !== zoom) {
              updateMap({
                // @ts-ignore
                zoom: googleMap.getZoom(),
              });
            }
          }}
          options={{
            clickableIcons: false,
            zoomControl: true,
            disableDefaultUI: true,
            keyboardShortcuts: false,
            draggable: true,
            styles: MAP_EXTRA_STYLES,
            gestureHandling: "greedy",
          }}
          // onClick={handleMapClick}
        >
          <div className="mob-main-map-container">
            {distributionLocations.length && (
              <AddressBlockV3
                addresses={distributionLocations}
                onAddressCardClick={onAddressCardClick}
                handleDeleteLocation={handleDeleteLocation}
                onPlaceChanged={onPlaceChanged}
                getFilteredPostCodes={getFilteredPostCodes}
                selectedRoutes={selectedRoutes}
                setSelectedRoutes={setSelectedRoutes}
                googleMap={googleMap}
                setShouldAutoSave={setShouldAutoSave}
                setIsUndoOrRedo={setIsUndoOrRedo}
                setIsSingleRouteSelection={setIsSingleRouteSelection}
                setShouldHideStatistics={setShouldHideStatistics}
                suggestedRoutes={suggestedRoutes}
                setDrawingMode={setDrawingMode}
              />
            )}
            {distributionLocations.length && isZoomedInForStatistics ? (
              isCircleSelectorActive ? (
                !shouldHideStatistics &&
                //@ts-ignore
                !!intersectedRoutesRef?.current?.size && (
                  <AreaStatisticsV2
                    isCalculatingRoutesIntercection={isCalculatingRoutesIntercection}
                    intersectedRoutesRef={intersectedRoutesRef}
                    mousePositionCoords={mousePositionCoords}
                    selectedTargets={selectedTargets}
                  />
                )
              ) : (
                <RouteStatisticsV2
                  route={hoveredRoute}
                  selectedRoutes={selectedRoutes}
                  costPerFlyer={costPerFlyer}
                  mousePositionCoords={mousePositionCoords}
                  selectedTargets={selectedTargets}
                />
              )
            ) : null}
            {/* Pin every location at any zoom so the entered address is visible among the routes. */}
            {!!distributionLocations.length &&
              distributionLocations.map((location) => {
                return (
                  <Marker
                    key={location.name}
                    position={{ lng: location.lng, lat: location.lat }}
                    onClick={() => {
                      setShouldAutoSave(true);
                      setIsUndoOrRedo(false);
                      setFocusedLocation(location);
                      setRadius(location.radius);
                    }}
                  />
                );
              })}
            <div className={"mob-next-button desk-hidden"} style={loading ? { pointerEvents: "none" } : {}}>
              {!isNextActive ? (
                <LightTooltip title={TooltipContent} placement="top">
                  <span>
                    <NextButton isNextActive={isNextActive} />
                  </span>
                </LightTooltip>
              ) : (
                <NextButton
                  loading={!clickSaveButtonRef.current}
                  isNextActive={isNextActive}
                  onClick={headerActions.NEXT.action}
                />
              )}
            </div>
          </div>
          {/* <DrawingManager
            onLoad={(loadedDrawingManager) => onDrawingManagerLoad({ loadedDrawingManager, setDrawingManager })}
            drawingMode={drawingMode}
            options={BROAD_AREAS_MAP_OPTIONS}
          /> */}
        </GoogleMap>
      </div>
    </div>
  );
};
